import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Typography, Affix, App, Modal, Form, Space, Switch, Image, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import { useNavigate, Link } from "react-router-dom"
import _ from "lodash"
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'
import { jsPDF } from "jspdf";
import { FaPlus, FaEdit, FaDownload, FaImage, FaTrash } from "react-icons/fa"
import { API, Enum, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

const currency = new Intl.NumberFormat()
const { Pages: { TicketManagement: { Company: CompanyLanguage } }, Enum: EnumLanguage, Share, Message: MessageLanguage } = language
const {  TICKET_MANAGEMENT_COMPANY_PAGINATE, TICKET_MANAGEMENT_COMPANY_REMOVE, TICKET_MANAGEMENT_COMPANY_UPDATE } = API
const { Message, Payment } = Enum
const { IRANSans, IRANSansFaNum } = Font

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [formModal] = Form.useForm()
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    })
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp()

    const companyColumns: ColumnsType<CompanyType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: CompanyLanguage.position,
            dataIndex: "position",
            key: "position",
            sorter: true,
        },
        {
            title: CompanyLanguage.number,
            dataIndex: "number",
            key: "number",
            sorter: true,
        },
        {
            title: CompanyLanguage.name,
            dataIndex: "name",
            key: "name",
            sorter: true,
        },
        {
            title: CompanyLanguage.tel,
            dataIndex: "tel",
            key: "tel",
            sorter: true,
            render: (text, record) => text && <Typography.Link href={`tel:${text}`}>{text}</Typography.Link>
        },
        {
            title: CompanyLanguage.cities,
            dataIndex: "cities",
            key: "cities",
            className: 'break-spaces',
            render: (text: CityType[], record) => text?.map(({ name: cityName, province: { name: provinceName } }) => `${cityName} (${provinceName})`).join(', ')
        },
        {
            title: CompanyLanguage.user,
            dataIndex: "user",
            key: "user",
            render: (text: UserType, record) => text && `${text.firstName} ${text.lastName} (0${text.mobile})`
        },
        {
            title: CompanyLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: CompanyLanguage.updatedAt,
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('HH:mm YYYY/MM/DD')
        },
        {
            title: Share.publish,
            dataIndex: 'publish',
            key: 'publish',
            render: (text, { id }) =>  (
                <Switch
                    checkedChildren={Share.active}
                    unCheckedChildren={Share.inactive}
                    checked={text}
                    style={{width:75}}
                    onClick={() => {
                        setLoadingRecords(true)
                        axios.patch(TICKET_MANAGEMENT_COMPANY_UPDATE + id, { publish: !text }).then((response: {}) => {
                            message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                            const { current, pageSize } = pagination
                            getCompanies({ page: current, limit: pageSize })
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error;
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response)
                            setLoadingRecords(false)
                        })
                    }}
                />
            )
        },
        {
            title: Share.edit,
            dataIndex: "edit",
            key: "edit",
            render: (text, record) =>  <Link to={'/ticket-management/company/edit/' + record.id}><FaEdit size={20} /></Link>
        },
        {
            title: Share.delete,
            dataIndex: "delete",
            key: "delete",
            render: (text, record) =>  (
                <Typography.Link onClick={() => modal.confirm({
                    title: Share.deleteConfirmTitle,
                    okText: Share.deleteConfirmOk,
                    okType: 'danger',
                    cancelText: Share.deleteConfirmCancel,
                    onOk() {
                        setLoadingRecords(true)
                        axios.delete(TICKET_MANAGEMENT_COMPANY_REMOVE + record.id).then((response: {}) => {
                            const { total = 1, pageSize = 1, current = 1 } = pagination
                            const params = {
                                page: total % pageSize !== 1 ? current : current - 1,
                                limit: pageSize,
                            }
                            getCompanies(params)
                            message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response )
                            setLoadingRecords(false)
                        })
                    }
                })}><FaTrash size={20} /></Typography.Link>
            )
        }
    ]

    const getCompanies = (params: AntTableParams) => {
        setLoadingRecords(true)
        axios.post(TICKET_MANAGEMENT_COMPANY_PAGINATE, null, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: companies, page: current, limit: pageSize, totalDocs: total } } = response
            let row = (current - 1) * pageSize
            setCompanies(companies.map<CompanyType>((company: CompanyType) => ({ ...company, row: ++row, key: company.id })))
            setLoadingRecords(false)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        const { current, pageSize } = pagination
        getCompanies({ page: current, limit: pageSize })
    }, [])

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filtersTable: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getCompanies({ page: current, limit: pageSize, sort })
    }

    return (
        <>
            <Table
                columns={companyColumns}
                dataSource={companies}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                    <Button key='add' type="primary" icon={<FaPlus />} style={{height:'40px'}} onClick={() => navigate('/ticket-management/company/add')}>{CompanyLanguage.addCompany}</Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
